import { useEffect, useRef, useState } from 'react';
import { Badge, Card, Alert, Button } from '@dreamtree-org/twreact-ui';
import { useStableApi } from '@/lib/api';

/**
 * The read side of app_api_audit for ONE scope — a single token (tokenId) or every
 * token bound to a workflow (workflowId). Shared by the workflow API tab
 * (components/workflow/ApiPanel.jsx) and the token detail screen
 * (pages/ApiTokenDetail.jsx); data comes from routes/apiUsageRouter.js (core:view).
 *
 * The requests table is offset-paginated server-side (limit/offset, newest first)
 * over the same 30-day window the totals count; the footer hides entirely until a
 * second page exists.
 */

const PAGE_SIZE = 50; // matches routes/apiUsageRouter.js's RECENT_LIMIT

function requestStatusBadge(code) {
  if (code >= 500) return <Badge variant="error" size="sm">{code}</Badge>;
  if (code >= 400) return <Badge variant="warning" size="sm">{code}</Badge>;
  if (code === 202) return <Badge variant="info" size="sm">{code}</Badge>;
  return <Badge variant="success" size="sm">{code}</Badge>;
}

export function RecentRequests({ requests }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-secondary-500 border-b border-[var(--app-border)]">
            <th className="py-2 pr-3 font-semibold">When</th>
            <th className="py-2 pr-3 font-semibold">Token</th>
            <th className="py-2 pr-3 font-semibold">Request</th>
            <th className="py-2 pr-3 font-semibold">Status</th>
            <th className="py-2 pr-3 font-semibold">Took</th>
            <th className="py-2 font-semibold">IP</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => (
            <tr key={r.id} className="border-b border-[var(--app-border)]/50 hover:bg-[var(--app-surface)]/50 transition-colors">
              <td className="py-2 pr-3 text-xs text-secondary-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
              <td className="py-2 pr-3 text-xs">
                <code className="font-mono text-[11px] bg-[var(--app-surface)] px-1.5 py-0.5 rounded border border-[var(--app-border)]/60">
                  {r.token_prefix}…
                </code>
              </td>
              <td className="py-2 pr-3 font-mono text-xs">{r.method} {r.path}</td>
              <td className="py-2 pr-3">
                {requestStatusBadge(r.status_code)}
              </td>
              <td className="py-2 pr-3 text-xs font-mono">{r.duration_ms == null ? '—' : `${r.duration_ms} ms`}</td>
              <td className="py-2 text-xs text-secondary-500 font-mono">{r.ip || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** ‹ Newer | Page N of M | Older › — the limit/offset pattern AgentFeedbackPage and
 *  PrescriptionsPage use. Hidden entirely while one page is all there is. */
function PageControls({ page, pageCount, hasOlder, onPage }) {
  return (
    <div className="flex items-center justify-end gap-3 pt-1 text-xs text-secondary-500">
      <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => onPage(page - 1)}>
        ‹ Newer
      </Button>
      <span className="text-xs whitespace-nowrap">Page {page + 1} of {pageCount}</span>
      <Button size="sm" variant="secondary" disabled={!hasOlder} onClick={() => onPage(page + 1)}>
        Older ›
      </Button>
    </div>
  );
}

function UsageStats({ totals }) {
  const t = totals || {};
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div className="p-3 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)]">
        <span className="text-[11px] font-medium tracking-wide uppercase text-secondary-500">Requests (30d)</span>
        <p className="text-xl font-semibold tracking-tight font-mono mt-1">{t.requests ?? 0}</p>
      </div>
      <div className="p-3 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)]">
        <span className="text-[11px] font-medium tracking-wide uppercase text-secondary-500">Errors</span>
        <p className={`text-xl font-semibold tracking-tight font-mono mt-1 ${t.errors > 0 ? 'text-rose-600 dark:text-rose-400' : ''}`}>
          {t.errors ?? 0}
        </p>
      </div>
      <div className="p-3 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)]">
        <span className="text-[11px] font-medium tracking-wide uppercase text-secondary-500">Avg duration</span>
        <p className="text-xl font-semibold tracking-tight font-mono mt-1">
          {t.avg_duration_ms == null ? '—' : `${t.avg_duration_ms} ms`}
        </p>
      </div>
      <div className="p-3 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)]">
        <span className="text-[11px] font-medium tracking-wide uppercase text-secondary-500">Last used</span>
        <p className="text-xs font-medium text-secondary-700 dark:text-secondary-300 mt-2 truncate" title={t.last_used_at ? new Date(t.last_used_at).toLocaleString() : 'never'}>
          {t.last_used_at ? new Date(t.last_used_at).toLocaleString() : 'never'}
        </p>
      </div>
    </div>
  );
}

/** The fetch half: summary + one page of requests, re-fetched per scope change and per
 *  page turn. A scope switch resets the page here so its offset can never point past
 *  (or into the middle of) the other scope's rows. */
function useUsageData({ fetchApi, tokenId, workflowId }) {
  // Convention (lib/apiDepConvention.test.js): fetchApi never goes in a dep array even
  // though useStableApi's is stable — calls go through a ref, deps stay real inputs.
  const fetchRef = useRef(fetchApi);
  fetchRef.current = fetchApi;
  const [page, setPage] = useState(0);
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const scopeKey = `${tokenId ?? ''}:${workflowId ?? ''}`;
  useEffect(() => { setPage(0); }, [scopeKey]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    const params = tokenId != null
      ? { token_id: tokenId, limit: PAGE_SIZE, offset: page * PAGE_SIZE }
      : { workflow_id: workflowId, limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    Promise.all([fetchRef.current('apiUsageSummary', params), fetchRef.current('apiUsageRequests', params)])
      .then(([s, r]) => {
        if (!alive) return;
        setSummary(s);
        setRequests((r && r.requests) || []);
        setTotal(Number(r && r.total) || 0);
      })
      .catch((ex) => alive && setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [scopeKey, page]);

  return { page, setPage, summary, requests, total, loading, err };
}

/** The empty-state line: page 0 with no data is "nothing yet"; a later page with no
 *  data means rows changed under the reader — hand them back to the newest. */
function emptyStateFor(idle, page, requests, resetToFirst) {
  if (!idle || requests.length) return null;
  if (page > 0) {
    return (
      <p className="text-xs text-secondary-500">
        No requests on this page — <button type="button" className="text-primary-600 hover:underline" onClick={resetToFirst}>back to the newest</button>.
      </p>
    );
  }
  return <p className="text-xs text-secondary-500">No requests recorded yet.</p>;
}

export default function UsageCard({ tokenId, workflowId, title = 'Usage (last 30 days)', className = '' }) {
  const { fetchApi } = useStableApi();
  const { page, setPage, summary, requests, total, loading, err } = useUsageData({ fetchApi, tokenId, workflowId });

  const idle = !loading && !err;
  // A short page also means "no more rows" — the last page can be shorter than a full
  // one, and the total can drift while paging (new requests arriving).
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isLastPage = page + 1 >= pageCount || requests.length < PAGE_SIZE;
  const showingControls = idle && total > PAGE_SIZE;
  const resetToFirst = () => setPage(0);

  return (
    <Card className={`p-4 space-y-2 ${className}`}>
      <h3 className="font-semibold text-sm">{title}</h3>
      {err && <Alert variant="error" title="Could not load usage">{err}</Alert>}
      {loading && <p className="text-xs text-secondary-500">Loading…</p>}
      {idle && <UsageStats totals={summary?.totals} />}
      {idle && requests.length > 0 && <RecentRequests requests={requests} />}
      {emptyStateFor(idle, page, requests, resetToFirst)}
      {showingControls && (
        <PageControls
          page={page}
          pageCount={pageCount}
          hasOlder={!isLastPage}
          onPage={setPage}
        />
      )}
    </Card>
  );
}
